import './ContentBuilderTemplate.scss';
import './ContentBuilderTemplate66.scss';

import React, { useEffect, useRef, useState } from 'react';
import { usePropsHandler } from '../../EventInterceptor/usePropsHandler';
import { cnContentBuilderTemplate } from './index';
import { Typography } from '@dataartdev/uikit/Typography';
import { Slider, SliderItem } from '@dataartdev/uikit/Slider';
import { Story } from '../../Story/Story';
import { Modal } from '@dataartdev/uikit/Modal';
import { StoryCard } from '../../StoryCard/StoryCard';
import { cn } from '@bem-react/classname';
import { IRoot66 } from './models/CTI_66.props';
import { SwiperOptions, Swiper } from 'swiper';

interface SwiperRef {
  swiper: Swiper;
}
interface SwiperElement extends Element, SwiperRef {}
interface ExtendedSwiperOptions extends SwiperOptions {
  onSlideChange: (swiper: Swiper) => void;
}

export const ContentBuilderTemplate: React.FC<IRoot66> = props => {
  const { type, groups = [], title, contentId } = usePropsHandler(
    cnContentBuilderTemplate(),
    props
  );
  const typeNumber = type.toString();

  const cnStories = cn('Stories');
  const cnStoriesPopup = cn('Stories-Popup');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItemId, setCurrentItemId] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentChildIndex, setCurrentChildIndex] = useState(0); // Index of the current child slider

  const parentSliderRef = useRef<SwiperRef>(null);
  const childSliderRef = useRef<SwiperRef>(null);

  const handleCardClick = (index: number) => {
    setCurrentSlide(index);
  };

  useEffect(() => {
    if (parentSliderRef.current?.swiper) {
      const swiper = parentSliderRef.current.swiper; // get swiper object from ref

      const handleSlideChange = () => {
        const currentSlideIndex = swiper.realIndex;
        setCurrentItemId(groups[currentSlideIndex].id);
        // setCurrentSlide(currentSlideIndex);
      };

      swiper.on('slideChange', handleSlideChange);

      swiper.slideTo(currentSlide, 0);

      return () => {
        swiper.off('slideChange', handleSlideChange);
      };
    }
  }, [currentSlide, groups]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isModalOpen) {
        localStorage.setItem(`viewed_${currentItemId}`, 'true');
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [isModalOpen, currentItemId]);

  const handleParentSlideChange = () => {
    const parentSwiper = parentSliderRef.current?.swiper;
    setCurrentChildIndex(prev =>
      parentSwiper ? parentSwiper.activeIndex : prev
    );
  };

  const handleNavigation = (isNext: boolean): void => {
    const parentSwiper =
      parentSliderRef.current && parentSliderRef.current.swiper;
    const currentChildElement = parentSwiper?.slides[currentChildIndex]
      .children[0] as SwiperElement;
    const currentChildSwiper = currentChildElement.swiper;

    if (isNext) {
      if (currentChildSwiper.isEnd && parentSwiper?.isEnd) {
        // Move to first parent slide and first child slide
        parentSwiper.slideTo(0);
        parentSwiper.slides.forEach(slide => {
          const childElement = slide.children[0] as SwiperElement;
          const childSwiper = childElement.swiper;
          childSwiper.slideTo(0);
        });
      } else if (currentChildSwiper.isEnd) {
        parentSwiper?.slideNext();
      } else {
        currentChildSwiper.slideNext();
      }
    } else {
      if (currentChildSwiper.isBeginning && parentSwiper?.isBeginning) {
        // Move to the last parent slide and its first child slide
        parentSwiper?.slideTo(parentSwiper.slides.length - 1, 0);
        const lastChildElement = parentSwiper?.slides[
          parentSwiper.slides.length - 1
        ].children[0] as SwiperElement;
        const lastChildSwiper = lastChildElement.swiper;

        lastChildSwiper.slideTo(0);
      } else if (currentChildSwiper.isBeginning) {
        parentSwiper?.slidePrev();
      } else {
        currentChildSwiper.slidePrev();
      }
    }
  };

  const storiesPreviewsSlider: SwiperOptions = {
    watchOverflow: true,
    navigation: false,
    slidesPerView: 2.5,
    spaceBetween: 24,
    breakpoints: {
      767: {
        slidesPerView: 4.35,
      },
      1200: {
        slidesPerView: 7,
      },
    },
  };

  const storiesSlider: ExtendedSwiperOptions = {
    onSlideChange: handleParentSlideChange,
  };

  const storyInnerSlider: SwiperOptions = {
    pagination: true,
    slidesPerView: 1,
    spaceBetween: 0,
  };

  return (
    <div className={cnStories({ type: typeNumber })} id={contentId}>
      <div className={cnStories()}>
        {title && <Typography.Title size="lg">{title}</Typography.Title>}
        <Slider {...storiesPreviewsSlider} className={cnStories('Slider')}>
          {groups?.map((group, index) => {
            return (
              <SliderItem key={group.id} onClick={() => handleCardClick(index)}>
                <div
                  className={cnStories('Story')}
                  onClick={() => {
                    setIsModalOpen(true);
                    setCurrentItemId(group.id);
                  }}
                >
                  <Story
                    {...group}
                    viewed={
                      localStorage.getItem(`viewed_${group.id}`) === 'true'
                    }
                  />
                </div>
              </SliderItem>
            );
          })}
        </Slider>
        <Modal
          hasOverlay
          onOpen={() => {
            document.body.style.overflow = 'hidden';
          }}
          onClose={() => {
            document.body.style.overflow = 'unset';
          }}
          isOpen={isModalOpen}
          onOverlayClick={(): void => {
            document.body.style.overflow = 'unset';
            setIsModalOpen(false);
            setCurrentSlide(0);
          }}
          onEsc={(): void => {
            setIsModalOpen(false);
            setCurrentSlide(0);
            document.body.style.overflow = 'unset';
          }}
          showCloseButton
          className={cnStoriesPopup()}
        >
          <div className={cnStoriesPopup('Inner')}>
            <button
              onClick={() => handleNavigation(false)}
              id="Stories-Popup-Prev"
              className={cnStoriesPopup('Move-General-Slider-Btn')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                fill="none"
              >
                <path fill="#0F161A" d="M12 12 6 6l6-6H6L0 6l6 6h6Z" />
              </svg>
            </button>
            <button
              onClick={() => handleNavigation(true)}
              id="Stories-Popup-Next"
              className={cnStoriesPopup('Move-General-Slider-Btn')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                fill="none"
              >
                <path fill="#0F161A" d="M12 12 6 6l6-6H6L0 6l6 6h6Z" />
              </svg>
            </button>
            <Slider
              ref={parentSliderRef}
              {...storiesSlider}
              className={cnStoriesPopup('Slider')}
            >
              {groups.map(group => (
                <SliderItem className={cnStoriesPopup('Story')} key={group.id}>
                  <Slider
                    ref={childSliderRef}
                    {...storyInnerSlider}
                    className={cnStoriesPopup('Story-Inner-Slider')}
                  >
                    {group?.stories?.map(story => (
                      <SliderItem
                        className={cnStoriesPopup('Inner-Slide')}
                        key={story.id}
                      >
                        <StoryCard {...story} />
                      </SliderItem>
                    ))}
                  </Slider>
                </SliderItem>
              ))}
            </Slider>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default ContentBuilderTemplate;
